import { Icon } from "@mdi/react";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { mdiAlertCircle } from "@mdi/js";
import { Button, Card, Tooltip } from "antd";
import { DEFAULT_NORMALIZE_BY } from "constants/chart.constants";
import { DEFAULT_TARGET, ErrorTypes } from "constants/ml.constants";
import {
  setMLNormalizationFeatureSetting,
  setTargetFeature
} from "store/features/ml/mlSlice";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { getProductDisplayName } from "utils/data-table";
import { getMlFeatureDisplayName } from "utils/ml/getMlFeatureDisplayName";

import { useUserUnit } from "hooks";

import { IconButton } from "../forecasting/Shared";
import GroupBy from "../groupBy";
import { NormalizeByToggle } from "./normalize";
import { FeatureImportanceSummary, MlTarget, NormalizeFeaturePackage } from "./types";
import {
  getNormalizedByLabel,
  removeIsolatedTrailingParentheses
} from "./utils/nameUtils";

interface TargetSelectorProps {
  setErrorModalVisible?: (visible: boolean) => void;
}

const TargetTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledAddFeatureButton = styled(Button)`
  width: 100%;
  min-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const TriggerContainer = styled.div`
  line-height: 1.2;
  font-weight: bold;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 7px;
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  .ant-button {
    height: auto;
  }
`;

const NormalizedByLabel = styled.span`
  font-size: 12px;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

export const TargetSelector: FC<TargetSelectorProps> = ({ setErrorModalVisible }) => {
  const dispatch = useDispatch();
  const unitType = useUserUnit();

  const mlState = useSelector((state: RootState) => state.ml);
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const target = (mlState.targetFeature as MlTarget)?.Property
    ? (mlState.targetFeature as MlTarget)
    : DEFAULT_TARGET;
  const [targetHasError, setTargetHasError] = useState(false);
  const normalizationItems =
    (mlState?.normalizationFeatures as NormalizeFeaturePackage[]) ?? [];
  const targetNormalizationItem = normalizationItems.find(
    (item) => item?.ApplyTo?.Property === target.Property
  );
  const targetNormalizationSetting = targetNormalizationItem?.NormalizeBy
    ? {
        ...DEFAULT_NORMALIZE_BY,
        field: targetNormalizationItem.NormalizeBy.Property,
        displayName: targetNormalizationItem.NormalizeBy.NiceName,
        unit:
          targetNormalizationItem.NormalizeBy.Unit ??
          targetNormalizationItem.NormalizeBy?.unit?.[unitType] ??
          DEFAULT_NORMALIZE_BY.unit,
        per: targetNormalizationItem.NormalizeBy.Value ?? DEFAULT_NORMALIZE_BY.per,
        useNormalizeBy: true
      }
    : {
        ...DEFAULT_NORMALIZE_BY,
        useNormalizeBy: false
      };

  const normalizationFeaturesFromSummary =
    (mlState?.featureImportanceSummary as FeatureImportanceSummary)?.well_tracking_data
      ?.normalizationSummary ?? [];
  const targetNormEntry = normalizationFeaturesFromSummary.find(
    (n) => n.ApplyTo?.Property === target?.Property
  );
  const targetNormLabel = getNormalizedByLabel(
    targetNormEntry?.NormalizeBy,
    unitType,
    categoryList
  );

  // Ensure store has target on mount (use default if store is empty)
  useEffect(() => {
    if (!(mlState.targetFeature as MlTarget)?.Property) {
      dispatch(setTargetFeature(DEFAULT_TARGET));
    }
  }, []);

  useEffect(() => {
    const featuresWithNaOrZero = mlState.warnDetails?.featuresWithNaOrZero || [];
    const featuresWithSingleValue = mlState.warnDetails?.featuresWithSingleValue
      ? Object.keys(mlState.warnDetails.featuresWithSingleValue)
      : [];

    if (
      (mlState.errorType === ErrorTypes.InvalidData &&
        featuresWithNaOrZero.includes(target.Property)) ||
      (mlState.errorType === ErrorTypes.SingularValue &&
        featuresWithSingleValue.includes(target.Property))
    ) {
      setTargetHasError(true);
    } else {
      setTargetHasError(false);
    }
  }, [target, mlState.errorType, mlState.errorMessage, mlState.warnDetails]);

  return (
    <Card>
      <TargetTitleContainer>
        <h5>Target Feature</h5>
        <span
          onClick={(event) => {
            // stop propagation to parent groupby
            event.stopPropagation();
          }}>
          <NormalizeByToggle
            displayBorder
            value={targetNormalizationSetting}
            hasFeatures={(mlState?.inputFeatures?.length ?? 0) > 0}
            onChange={(next) => {
              const normalizeByFeature: NormalizeFeaturePackage["NormalizeBy"] | null =
                next?.useNormalizeBy
                  ? {
                      Property: next.field || DEFAULT_NORMALIZE_BY.field,
                      NiceName:
                        next.displayName || next.field || DEFAULT_NORMALIZE_BY.field,
                      CanBeZero: true,
                      Monotonic: 0,
                      checked: true,
                      category: "",
                      unit: next.units,
                      Value: next.per,
                      Unit: next.unit
                    }
                  : null;

              dispatch(
                setMLNormalizationFeatureSetting({
                  applyTo: {
                    Property: target.Property,
                    NiceName: target.NiceName,
                    CanBeZero: true,
                    Monotonic: 0,
                    checked: true,
                    category: "",
                    unit: target.AllUnits,
                    dataType: target.dataType
                  },
                  normalizeBy: normalizeByFeature
                })
              );
            }}
          />
        </span>
      </TargetTitleContainer>
      <GroupBy
        value={{ property: DEFAULT_TARGET.Property, title: DEFAULT_TARGET.NiceName }}
        dataTypeFilters={["Number", "Integer"]}
        hideDateRange
        onChange={(value) => {
          const rawTitle = value.product
            ? `${getProductDisplayName(value.product)}: ${value.title}`
            : value.title;
          const niceName = value.units
            ? removeIsolatedTrailingParentheses(rawTitle)
            : rawTitle;
          dispatch(
            setTargetFeature({
              Property: value.property,
              NiceName: niceName,
              Unit: value?.units?.[unitType] ?? value?.defaultUnit ?? "",
              AllUnits: value?.units,
              dataType: value?.dataType,
              CanBeZero: true
            } as MlTarget)
          );
        }}>
        <TriggerContainer>
          <NameContainer>
            {getMlFeatureDisplayName(target, categoryList)}
            {targetHasError ? (
              <Tooltip
                title={
                  "Target feature contains invalid well data. Click to view details."
                }>
                <IconButton
                  type="text"
                  danger
                  onClick={(e) => {
                    e.stopPropagation();
                    setErrorModalVisible(true);
                  }}
                  icon={<Icon path={mdiAlertCircle} size={"16px"} color="#f0ad4e" />}
                />
              </Tooltip>
            ) : null}
          </NameContainer>
          {targetNormLabel ? (
            <NormalizedByLabel>{targetNormLabel}</NormalizedByLabel>
          ) : null}
          <StyledAddFeatureButton>Change Target Feature</StyledAddFeatureButton>
        </TriggerContainer>
      </GroupBy>
    </Card>
  );
};
